import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMessageStore } from "@/store/messages";
import { useProfileStore, getFacilityKey, getPatientKey } from "@/store/profile";
import msgRequest from "@/api/msg-request";
import { formatDate } from "@/utils/format";
import { toast } from "sonner";
import { NewMessageForm } from "@/components/messages/NewMessageForm";

interface MessageRow {
  id: number;
  subject: string;
  fromPatient?: boolean;
  fromName?: string;
  date: string;
  read?: boolean;
  parentID: number;
  archived?: boolean;
}

/** Normalize message from API (may use parentId, ParentID, etc.). */
function normalizeMessage(raw: Record<string, unknown>): MessageRow {
  const parentID =
    raw.parentID ?? raw.parentId ?? (raw as { ParentID?: number }).ParentID ?? 0;
  const archived =
    raw.archived ?? (raw as { Archived?: boolean }).Archived ?? false;
  return {
    id: Number(raw.id ?? raw.Id ?? 0),
    subject: String(raw.subject ?? raw.Subject ?? ""),
    fromPatient: Boolean(
      raw.fromPatient ?? (raw as { FromPatient?: boolean }).FromPatient
    ),
    fromName: String(
      raw.fromName ?? (raw as { FromName?: unknown }).FromName ?? ""
    ),
    date: String(raw.date ?? raw.Date ?? ""),
    read: raw.read != null
      ? Boolean(raw.read)
      : (raw as { Read?: boolean }).Read,
    parentID: Number(parentID),
    archived: Boolean(archived),
  };
}

function getFromName(msg: MessageRow, officeName: string): string {
  return msg.fromPatient ? (msg.fromName ?? "") : officeName;
}

export interface MsgListProps {
  variant?: "full" | "panel";
}

export function MsgList({ variant = "full" }: MsgListProps) {
  const currentPatient = useProfileStore((s) => s.currentPatient);
  const currentMsgInfo = useMessageStore((s) => s.currentMsgInfo);
  const messageListVersion = useMessageStore((s) => s.messageListVersion);
  const setMsgCurrentPage = useMessageStore((s) => s.setMsgCurrentPage);
  const setCurrentMsgInfo = useMessageStore((s) => s.setCurrentMsgInfo);
  const createMessageOpen = useMessageStore((s) => s.createMessageOpen);
  const setCreateMessageOpen = useMessageStore(
    (s) => s.setCreateMessageOpen
  );

  const [messages, setMessages] = useState<MessageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);

  const loadMessages = async () => {
    const facilityKey = getFacilityKey();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    setLoading(true);
    try {
      const res = await msgRequest.request.getMessageList(
        facilityKey,
        patientKey,
        { includeArchived: true }
      );
      const raw = res?.data;
      const list = Array.isArray(raw) ? raw : (raw as Record<string, unknown>)?.data ?? raw ?? [];
      const listArray = Array.isArray(list) ? list : [];
      setMessages(listArray.map((m: Record<string, unknown>) => normalizeMessage(m)));
    } catch {
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const loadArchivedCount = async () => {
    const facilityKey = getFacilityKey();
    const patientKey = getPatientKey();
    if (!facilityKey || !patientKey) return;
    try {
      const res = await msgRequest.request.getArchivedMessageCount(
        facilityKey,
        patientKey
      );
      setArchivedCount(Number(res?.data) || 0);
    } catch {
      setArchivedCount(0);
    }
  };

  useEffect(() => {
    loadMessages();
    loadArchivedCount();
  }, [currentPatient?.facilityID, currentPatient?.patientID, messageListVersion]);

  const showPanelCreatePortal =
    createMessageOpen && variant === "panel";

  useEffect(() => {
    if (!createMessageOpen) return;
    if (variant === "full") {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    if (variant !== "panel") return;
    const mq = window.matchMedia("(max-width: 767.98px)");
    const apply = () => {
      if (mq.matches) document.body.style.overflow = "hidden";
      else document.body.style.overflow = "";
    };
    apply();
    mq.addEventListener("change", apply);
    return () => {
      mq.removeEventListener("change", apply);
      document.body.style.overflow = "";
    };
  }, [createMessageOpen, variant]);

  const filteredList = messages.filter((m) => {
    const isRoot = m.parentID === 0 || m.parentID === undefined;
    if (!isRoot) return false;
    if (showArchived) return true;
    return !m.archived;
  });

  const archive = async (id: number) => {
    const facilityKey = getFacilityKey();
    if (!facilityKey) return;
    try {
      await msgRequest.request.archiveMessage(facilityKey, { id: String(id) });
      toast.success("Message archived");
      loadArchivedCount();
      loadMessages();
    } catch {
      toast.error("Failed to archive");
    }
  };

  const startCreate = () => {
    setCreateMessageOpen(true);
    setCurrentMsgInfo(null);
    setMsgCurrentPage("MsgList");
  };

  const viewMsg = (msg: MessageRow) => {
    setCreateMessageOpen(false);
    setCurrentMsgInfo({ id: msg.id, subject: msg.subject });
    setMsgCurrentPage("MsgDetails");
  };

  if (!currentPatient) return null;

  const officeName = currentPatient.officeName ?? "";
  const selectedId = currentMsgInfo?.id != null ? Number(currentMsgInfo.id) : null;

  const newMessageMobilePortal =
    showPanelCreatePortal && typeof document !== "undefined"
      ? createPortal(
          <div
            className="fixed z-[200] max-md:bottom-0 max-md:left-0 max-md:right-0 max-md:top-[calc(4rem+env(safe-area-inset-top,0px))] max-md:overflow-y-auto max-md:bg-white max-md:pb-[env(safe-area-inset-bottom,0px)] md:hidden"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-msg-title"
            onClick={(e) => {
              if (e.target === e.currentTarget) setCreateMessageOpen(false);
            }}
          >
            <div
              className="mx-auto flex w-full min-w-0 max-w-lg flex-1 min-h-0 min-h-[calc(100dvh-4rem-env(safe-area-inset-top,0px))] flex-col overflow-y-auto p-3 2xs:p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <NewMessageForm
                onCancel={() => setCreateMessageOpen(false)}
                className="w-full"
              />
            </div>
          </div>,
          document.body
        )
      : null;

  if (variant === "panel") {
    return (
      <>
        <div className="flex flex-col gap-2 pb-2">
          <button
            type="button"
            onClick={startCreate}
            className="min-h-11 w-full rounded-lg bg-healthsource-red py-2 text-sm font-bold text-white hover:opacity-90"
          >
            Create Message
          </button>
          {archivedCount > 0 && (
            <label className="flex min-h-11 cursor-pointer items-center gap-2.5 px-2 max-sm:px-0">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => {
                  setShowArchived(e.target.checked);
                  loadArchivedCount();
                }}
                className="h-5 w-5 shrink-0 rounded border-healthsource-lightLavender accent-healthsource-navy"
              />
              <span className="text-sm font-semibold text-healthsource-gray">
                Show archived
              </span>
            </label>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden">
          {loading && (
            <p className="px-2 py-2 text-[13px] text-healthsource-gray max-sm:px-0">
              Fetching messages…
            </p>
          )}
          {!loading && filteredList.length === 0 && (
            <p className="px-2 py-3 text-[15px] text-healthsource-gray max-sm:px-0">
              No messages yet. Questions for the office? Create a message.
            </p>
          )}
          {!loading &&
            filteredList.map((msg) => {
              const isActive = selectedId === msg.id;
              return (
                <button
                  key={msg.id}
                  type="button"
                  onClick={() => viewMsg(msg)}
                  className="flex flex-col rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-healthsource-navy/10 max-sm:!border-l-0 max-sm:!px-0 2xs:px-2.5 xs:px-3"
                  style={{
                    background: isActive
                      ? "rgba(42,64,100,0.1)"
                      : "transparent",
                    borderLeft: `3px solid ${isActive ? "#2A4064" : "transparent"}`,
                  }}
                >
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className={`truncate text-[15px] ${!msg.read ? "font-extrabold" : "font-semibold"} text-healthsource-dark`}
                    >
                      {getFromName(msg, officeName)}
                    </span>
                    {!msg.read && (
                      <span
                        className="h-2 w-2 flex-shrink-0 rounded-full bg-healthsource-navy"
                        aria-hidden
                      />
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-[13px] text-healthsource-gray">
                    {msg.subject}
                  </p>
                  <p className="mt-1 text-[13px] text-healthsource-gray">
                    {formatDate(msg.date)}
                  </p>
                </button>
              );
            })}
        </div>
        {newMessageMobilePortal}
      </>
    );
  }

  return (
    <div className="space-y-4">
      {loading && (
        <p className="text-muted-foreground">Fetching messages …</p>
      )}
      {!loading && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4">
            {archivedCount > 0 && (
              <label className="flex cursor-pointer items-center gap-2">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked);
                    loadArchivedCount();
                  }}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm">Show Archived Messages</span>
              </label>
            )}
            <button
              type="button"
              onClick={startCreate}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
            >
              Create Message
            </button>
          </div>

          <div className="rounded-lg border border-border overflow-hidden">
            {filteredList.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground">
                Message list empty.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="p-3 text-left font-medium">Subject</th>
                      <th className="p-3 text-left font-medium">From</th>
                      <th className="p-3 text-left font-medium">Date</th>
                      <th className="p-3 w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map((msg) => (
                      <tr
                        key={msg.id}
                        className="border-b border-border hover:bg-muted/30"
                      >
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => viewMsg(msg)}
                            className={`text-left underline hover:no-underline ${!msg.read ? "font-semibold" : ""}`}
                          >
                            {msg.subject}
                          </button>
                        </td>
                        <td
                          className={`p-3 ${!msg.read ? "font-semibold" : ""}`}
                        >
                          {getFromName(msg, officeName)}
                        </td>
                        <td className="p-3">{formatDate(msg.date)}</td>
                        <td className="p-3">
                          <button
                            type="button"
                            onClick={() => archive(msg.id)}
                            title="Archive"
                            className="text-muted-foreground hover:text-foreground"
                          >
                            Archive
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {createMessageOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-msg-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setCreateMessageOpen(false);
          }}
        >
          <div
            className="my-auto w-full min-w-0 max-w-lg overflow-y-auto rounded-xl border border-healthsource-lightLavender bg-white p-4 py-1 shadow-2xl max-h-[min(90dvh,48rem)]"
            onClick={(e) => e.stopPropagation()}
          >
            <NewMessageForm
              onCancel={() => setCreateMessageOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
